import React, { useCallback, useEffect, useState } from 'react';
import { $showStartPage, applicationGate, closeStartPage } from 'domains/application';
import { useGate, useUnit } from 'effector-react';
import { FEHeader } from 'ui-kit/layouts';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';
import {
  $type,
  $address,
  $restaurant,
  $showAddressConfirmation,
  closeAddressConfirmation,
  $city,
  $country,
  $cityInformationBanner,
  closeCityInformationBanner,
} from 'domains/cartography';
import { $bonusAccount } from 'domains/profile';
import { AuthClient } from 'api/base';
import {
  $savedAddresses,
  getSavedAddressesFx,
} from 'domains/savedAddresses';
import { usePrepareLink } from 'hooks/router';
import { Language as LanguageModel, OrderType } from 'models';
import style from './style.module.css';
import { $catalogCategories, $catalogProducts } from 'domains/catalog';
// import { $cityPhoneData } from 'domains/catalog';
import { Location } from 'react-router-dom';
import PromotionsPage from 'pages/promotions';
import {
  CONTACTS_PATHNAME,
  DELIVERY_AND_PAYMENTS_PATHNAME,
  GET_ENUMS,
  GET_PARAMS,
  HOTLINE_PHONE_NUMBER,
  LEGAL_INFORMATION_PATHNAME,
  PROFILE_PATHNAME,
  PROMOTIONS_PATHNAME,
  RESTAURANTS_PATHNAME,
  SAVED_CARDS_PATHNAME,
} from 'const';
import LegalInformationPage from 'pages/legalInformation';
import { Country, Language, CityInformationBanner } from './components';
import Footer from '../footer';
import { $language, changeLanguage } from 'domains/language';
import { $sitesMap } from 'domains/sitesMap';
import { useTranslation } from 'react-i18next';
import { CookiesLayot } from '../cookiesLayot';
import { FELocalStorageUtil } from 'utils';
import { Helmet } from 'react-helmet';
import useMediaQuery from '../../shared/ui-kit/hooks/useMediaQuery';
import FEMobileHeader from 'layouts/FEMobileHeader';
import clsx from 'clsx';
import { SiteSeo } from '../../shared/seo/SiteSeo';

const ApplicationLayout = ({
  children,
}: {
  children: ({ location }: { location: Location }) => React.ReactNode;
}) => {
  const { i18n, t } = useTranslation();
  useGate(applicationGate);
  const isDesktop = useMediaQuery(`(min-width: ${500}px)`);
  const isLaptopOrMobile = useMediaQuery(`(max-width: ${1024}px)`);

  const [show, setShow] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  const [type, address, restaurant, city, country] = useUnit([
    $type,
    $address,
    $restaurant,
    $city,
    $country,
  ]);

  const bonusAccount = useUnit($bonusAccount);

  const [showAddressConfirmation, onCloseConfrimation] = useUnit([
    $showAddressConfirmation,
    closeAddressConfirmation,
  ]);

  const [showStartPage, onClosePage] = useUnit([
    $showStartPage,
    closeStartPage,
  ]);

  const [products, categories, sitesMap] = useUnit([
    $catalogProducts,
    $catalogCategories,
    // $cityPhoneData,
    $sitesMap,
  ]);

  const [language, onChangeLanguage] = useUnit([$language, changeLanguage]);

  const handleChangeLanguage = (lng: LanguageModel) => {
    onChangeLanguage(lng);
    i18n.changeLanguage(lng);
  };

  const match = useMatch('/:name');

  const showPromotionsPage = React.useMemo(
    () => location.pathname === `${PROMOTIONS_PATHNAME}`,
    [location],
  );
  const showLegalInformationPage = React.useMemo(
    () => location.pathname === `${LEGAL_INFORMATION_PATHNAME}`,
    [location],
  );
  
  const [cityInformationBanner, handleCloseCityInformationBanner] = useUnit([
    $cityInformationBanner,
    closeCityInformationBanner,
  ]);

  const cartographyLink = usePrepareLink({
    query: {
      [GET_PARAMS.page]: GET_ENUMS.page.cartography,
    },
    keepOldQuery: true,
  });

  const [savedAddresses, addressesPending] = useUnit([
    $savedAddresses,
    getSavedAddressesFx.pending,
  ]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const openAddressFlow = () => {
    if (pickerOpen) {
      setPickerOpen(false);
      return;
    }
    if (
      AuthClient.isAuth &&
      (savedAddresses.length > 0 || addressesPending)
    ) {
      setPickerOpen(true);
      return;
    }
    navigate(cartographyLink);
  };

  const closePicker = () => setPickerOpen(false);

  const openCartography = useCallback(() => {
    setPickerOpen(false);
    navigate(cartographyLink);
  }, [navigate, cartographyLink]);

  const countryLayoutLink = usePrepareLink({
    query: {
      [GET_PARAMS.page]: GET_ENUMS.page.cartography,
    },
    pushToQuery: {
      [GET_PARAMS.layout]: 'country',
    },
    keepOldQuery: true,
  });

  const controlNavbar = () => {
    if (window.scrollY > 0 && !isDesktop) {
      setShow(false);
    } else {
      setShow(true);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', controlNavbar);

    return () => {
      window.removeEventListener('scroll', controlNavbar);
    };
  }, [lastScrollY]);

  

  React.useEffect(() => {
    const rawPath = decodeURIComponent(location.pathname);
    const pathName = rawPath === '/' ? '/' : rawPath.replace(/\/$/, '');
    
    if (pathName === '/') return;

    if (products && categories) {
      
      const foundProduct = [
        ...products.constructors,
        ...products.dishes,
      ].find((product) => {
        const productPathName = `/${product.linkValue.value}`;
        return productPathName === pathName;
      });
      
      const foundCategory = categories.find(
        (category) => `/${category.linkValue}` === pathName,
      );

      const isStaticPath = [
        PROFILE_PATHNAME,
        DELIVERY_AND_PAYMENTS_PATHNAME,
        RESTAURANTS_PATHNAME,
        CONTACTS_PATHNAME,
        LEGAL_INFORMATION_PATHNAME,
        PROMOTIONS_PATHNAME,
        SAVED_CARDS_PATHNAME
      ].includes(pathName);

      if (!foundProduct && !foundCategory && !isStaticPath) {
        navigate('404', { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, categories, location.pathname, navigate]);

  return (
    <>
      <Helmet>
        <title>{t('meta.index.title')}</title>
        <meta name='title' content={t('meta.index.metaTitle')} />
        <meta name='description' content={t('meta.index.metaDescription')} />
        <meta name='Keywords' content={t('meta.index.metaKeywords')} />
        <meta property='og:title' content={t('meta.index.metaTitle')} />
        <meta
          property='og:description'
          content={t('meta.index.metaDescription')}
        />
        <meta name='twitter:title' content={t('meta.index.metaTitle')} />
        <meta
          name='twitter:description'
          content={t('meta.index.metaDescription')}
        />
      </Helmet>
      <SiteSeo />
      <div className={show ? style.languageLayout : style.hideLayout}>
        <div className={style.languageLayoutContainer}>
          <Language
            language={language}
            onChangeLanguage={handleChangeLanguage}
            className={style.languageButton}
          />
          <Country
            city={city?.name || null}
            country={country?.name || null}
            onClick={() => navigate(countryLayoutLink)}
          />
        </div>
        <div className={style.deliveryAndPhoneContainer}>
          {sitesMap?.franchise && (
            <a
              target='_blank'
              rel='noreferrer'
              href={sitesMap.franchise}
              className={style.informationListItem}
            >
              {t('footer.information.franchise')}
            </a>
          )}
          {null /*
          <a
            className={clsx(
              'headline',
              style.dataValue,
              !isDesktop && style.deliveryAndPhoneContainerHide,
            )}
            href={`tel:${cityPhoneData.phoneNumber}`}
          >
            {cityPhoneData?.phoneNumber || ''}
          </a>
          */}
          <Link
            className={clsx(
              style.informationListItem,
              !isDesktop && style.deliveryAndPhoneContainerHide,
            )}
            to={`${DELIVERY_AND_PAYMENTS_PATHNAME}`}
            onClick={() =>
              window.scrollTo({
                top: 0,
              })
            }
          >
            {t('footer.information.deliveryAndPayment')}
          </Link>
        </div>
      </div>

      {(isDesktop ? (
        <FEHeader
          onLogoClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            navigate('/');
          }}
          address={address?.address || null}
          restaurant={restaurant?.name || null}
          orderType={type as Exclude<OrderType, 'IN_PLACE'>}
          onAddressBarClick={openAddressFlow}
          onCloseConfirmation={() => onCloseConfrimation()}
          onDeclineConfirmation={openAddressFlow}
          isOpenConfirmation={showAddressConfirmation}
          isOpenStartAppConfirmation={showStartPage}
          isOpenAddressPicker={pickerOpen}
          onCloseAddressPicker={closePicker}
          onAddAddress={openCartography}
          onAddressPickerEmpty={openCartography}
        />
      ) : (
        <FEMobileHeader
          bonusAccount={bonusAccount}
          address={address?.address || null}
          restaurant={restaurant?.name || null}
          orderType={type as Exclude<OrderType, 'IN_PLACE'>}
          onAddressBarClick={openAddressFlow}
          onCloseConfirmation={() => onCloseConfrimation()}
          onDeclineConfirmation={openAddressFlow}
          isOpenConfirmation={showAddressConfirmation}
          isOpenStartAppConfirmation={showStartPage}
          isOpenAddressPicker={pickerOpen}
          onCloseAddressPicker={closePicker}
          onAddAddress={openCartography}
          onAddressPickerEmpty={openCartography}
        />
      ))}
      {children({ location })}
      <Footer />
      <PromotionsPage onClose={() => navigate(-1)} open={showPromotionsPage} />
      <LegalInformationPage
        onClose={() => navigate(-1)}
        open={showLegalInformationPage}
      />
      <CookiesLayot
        onClose={() => FELocalStorageUtil.setShowCookies(false)}
        show={FELocalStorageUtil.showCookies}
      />
      <CityInformationBanner
        onClose={handleCloseCityInformationBanner}
        data={cityInformationBanner}
      />
    </>
  );
};

export default ApplicationLayout;
